import { useState } from "react";
import { X, Moon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Dialog "Mettre en stand-by un dossier".
 *
 * Motif obligatoire (libre + suggestions), date de reprise optionnelle.
 * Appelle POST /dossiers/{id}/standby.
 */
const REASON_SUGGESTIONS = [
  "Ne répond plus",
  "Projet reporté",
  "Attente compromis",
  "Attente documents",
  "Client pas mûr",
  "Autre",
];

export default function StandbyDialog({ dossier, open, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open || !dossier) return null;

  const submit = async () => {
    const cleaned = (reason || "").trim();
    if (!cleaned) {
      toast.error("Précise le motif");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/dossiers/${dossier.id}/standby`, {
        reason: cleaned,
        until: until || null,
      });
      toast.success("Dossier mis en stand-by");
      onDone?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, calc(env(safe-area-inset-bottom) + 1rem))",
      }}
      data-testid="standby-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col"
        style={{ maxHeight: "calc(100dvh - 2rem - env(safe-area-inset-bottom))" }}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Moon size={16} weight="fill" color="var(--cf-accent)" />
            <h2 className="font-display text-base font-extrabold text-slate-900">Mettre en stand-by</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100"
            data-testid="standby-dialog-close"
          >
            <X size={14} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Dossier</div>
            <div className="text-[14px] font-bold text-slate-900 truncate">{dossier.nom}</div>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 block">
              Motif <span className="text-rose-500">*</span>
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {REASON_SUGGESTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                    reason === r
                      ? "bg-violet-100 border-violet-300 text-violet-800"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`standby-reason-suggest-${r.replace(/\s/g, "_")}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Saisis librement ou choisis ci-dessus"
              className="w-full text-[13px] border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[color:var(--cf-primary)]"
              data-testid="standby-reason-input"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 block">
              Date de reprise <span className="text-slate-400 font-normal lowercase">(facultatif)</span>
            </label>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="w-full text-[13px] border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-[color:var(--cf-primary)]"
              data-testid="standby-until-input"
            />
            <div className="text-[10.5px] text-slate-400 mt-1">
              Si vide, le dossier reste en sommeil jusqu'à réactivation manuelle.
            </div>
          </div>
        </div>

        <footer className="px-4 py-3 border-t border-slate-100 flex-shrink-0 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-[13px] font-bold text-slate-600 hover:bg-slate-100"
            data-testid="standby-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-[13px] font-bold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="standby-submit"
          >
            <Moon size={12} weight="fill" />
            {saving ? "..." : "Mettre en stand-by"}
          </button>
        </footer>
      </div>
    </div>
  );
}
